import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { PortfolioProject } from '../data/portfolioProjects';

interface ProjectDetailViewProps {
  project: PortfolioProject;
  onBack: () => void;
  onToggle2D: () => void;
}

export default function ProjectDetailView({ project, onBack, onToggle2D }: ProjectDetailViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when component mounts or project changes
  useEffect(() => {
    // Scroll the main window to top
    window.scrollTo({ top: 0, behavior: 'instant' });
    
    // Also scroll the detail view container to top
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [project.id]);

  // Prevent body scroll when detail view is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-night-900/95 backdrop-blur-lg overflow-auto"
    >
      <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Navigation */}
          <div className="flex items-center justify-between mb-12">
            <motion.button
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              onClick={onBack}
              className="flex items-center gap-2 text-offwhite/80 hover:text-white transition-colors group"
            >
              <ArrowLeft className="group-hover:-translate-x-1 transition-transform" size={20} />
              Back to Portfolio
            </motion.button>

            <motion.button
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              onClick={onToggle2D}
              className="text-sm text-offwhite/60 hover:text-launch-blue transition-colors"
            >
              Switch to 2D View
            </motion.button>
          </div>

          {/* Project Header */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${project.color}20` }}
              >
                <div
                  className="w-12 h-12 rounded-full"
                  style={{ backgroundColor: project.color }}
                />
              </div>
              <div>
                <h1
                  className="text-4xl md:text-5xl font-bold mb-2"
                  style={{ color: project.color }}
                >
                  {project.title}
                </h1>
                <p className="text-launch-purple text-lg">{project.category}</p>
              </div>
            </div>
          </motion.div>

          {/* Project Screenshot */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="mb-12"
          >
            <div className="relative rounded-2xl overflow-hidden border-2" style={{ borderColor: `${project.color}30` }}>
              <img 
                src={project.image} 
                alt={`${project.title} screenshot`}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </motion.div>

          {/* Project Description */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-4">Overview</h2>
            <p className="text-offwhite/80 text-lg leading-relaxed">
              {project.description}
            </p>
          </motion.div>

          {/* Tech Stack */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Technology Stack</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {project.tech.map((tech, index) => (
                <motion.div
                  key={tech}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.05 }}
                  className="bg-night-800 border border-night-700 rounded-lg px-4 py-3 text-center hover:border-launch-blue/50 transition-colors"
                >
                  <span className="text-offwhite/90 font-medium text-sm">{tech}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-white mb-4">Results & Impact</h2>
            <div
              className="bg-gradient-to-br from-night-800 to-night-900 border rounded-xl p-8"
              style={{ borderColor: `${project.color}30` }}
            >
              <p className="text-offwhite/90 text-lg leading-relaxed">
                {project.results}
              </p>
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-4"
          >
            {project.url ? (
              <a
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  backgroundColor: project.color,
                  color: 'white',
                }}
              >
                View Live Site
                <ExternalLink size={16} />
              </a>
            ) : (
              <button
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all hover:scale-105"
                style={{
                  backgroundColor: project.color,
                  color: 'white',
                }}
              >
                View Case Study
                <ExternalLink size={16} />
              </button>
            )}
            <Link
              to="/contact"
              className="px-6 py-3 bg-night-800 border border-night-700 rounded-lg text-white font-semibold hover:border-launch-blue/50 transition-all inline-block text-center"
            >
              Contact Us About This Project
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
